import { useEffect, useState } from 'react';
import { Config } from '../../config';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { Main } from '../../components/Main/Main';
import { CreateSubscriptionModal } from '../../components/subscriptions/CreateSubscriptionModal';
import { Subscription } from '../../components/subscriptions/Subscription';
import { useImmer } from '../../hooks/useImmer';
import { useTranslation } from '../../i18n/i18n';
import { fetchResourceMap, optionalResource, PreloadedData, useApi, useCms } from '../../state/api';
import { Res } from '../../state/api-resources';
import { ApiList } from '../../types';
import { CmsComponent, isRenderable } from '../cms/CmsComponent';
import { Routing } from '../Routes';
import { ErrorPage } from './ErrorPage';
import { LoaderPage } from './LoaderPage';
import './SubscriptionsPage.css';

/* the two blocks this page lets the cms own, top and bottom; a piece renders only where it is asked for by name */
const TOP_PIECE = 'on-repeat-top';
const BOTTOM_PIECE = 'on-repeat-bottom';

export const Subscriptions = (): JSX.Element => {
	const [subscriptions, setSubscriptions] = useImmer<ApiList[] | undefined>(undefined);
	const { data, error, mutate } = useApi(Res.lists, {});
	/* the subscription the wizard is seeing through, which is always one that was just named */
	const [wizardListId, setWizardListId] = useState<string | null>(null);
	const t = useTranslation().subscriptions;
	const { data: topContent } = useCms(Res.cmsComponent, { slug: TOP_PIECE });
	const { data: bottomContent } = useCms(Res.cmsComponent, { slug: BOTTOM_PIECE });

	useEffect(() => {
		setSubscriptions(data);
	}, [data]);

	const refresh = () => mutate(subscriptions, true);

	/*
	 * The subscription that was just created, as the api named it (kg-api #82). The wizard opens on
	 * its card, so the list has to come back first.
	 */
	const startWizardForNewSubscription = async (listId: string | null, stillGuided: () => boolean) => {
		await mutate();

		// asked again on this side of the revalidation: the customer may be naming the next one by now
		if (listId && stillGuided()) setWizardListId(listId);
	};

	if (error) return <ErrorPage message={error.message} />;
	if (!subscriptions) return <LoaderPage />;

	return (
		<Main>
			{/*
			 * The menu names the page, the way the design has it, so the heading is not written out
			 * a second time under it - but a page still has to be able to say what it is to a
			 * screen reader, where a navigation item is a link and not a title.
			 */}
			<h1 className="visually-hidden">{t.subscriptionTitle}</h1>
			<AccountPageNavigation />
			{/* an emptied piece is published as [], which renders nothing - and must not leave its spacing behind */}
			{isRenderable(topContent) ? <CmsComponent structure={topContent} /> : null}
			<div className="subscriptions-page">
				{subscriptions.map((list) => (
					<Subscription
						key={list.id}
						list={list}
						updateSubscriptions={refresh}
						startWizard={list.id === wizardListId}
						onWizardEnd={() => setWizardListId(null)}
					/>
				))}
			</div>
			<div className="subscriptions-footer">
				<CreateSubscriptionModal
					onCreate={startWizardForNewSubscription}
					subscriptionsCount={subscriptions.length}
				/>
				<p className="disclaimer">{t.schedule.dispensationText}</p>
			</div>
			{isRenderable(bottomContent) ? <CmsComponent structure={bottomContent} /> : null}
		</Main>
	);
};

export const subscriptionsRouting: Routing = {
	path(urls) {
		return urls.subscriptions;
	},
	component: Subscriptions,
	requiresAuth: true,
	preload: async (config: Config): Promise<PreloadedData> => {
		const pieces = await Promise.all(
			[TOP_PIECE, BOTTOM_PIECE].map((slug) =>
				optionalResource(fetchResourceMap(config, Res.cmsComponent, { slug }, config.cmsContent))
			)
		);

		return Object.assign({}, ...pieces);
	},
};
